/* Copyright (c) 2025 Xperi. All Rights Reserved.  */

import {
    Configuration,
    ConfigurationParameters,
    GuideApi,
    GuideRowsGetV2Request,
    OneLineGuideApi,
    OneLineGuideCellsGetV2Request,
} from 'client-code/grid-guide';

import { APPLICATION_FEATURE_AREA } from '../../config';
import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { DeviceModel } from '../device/DeviceModel';
import { OneLineGuideRow } from '../entities';
import { GridGuideRow } from '../entities/GridGuideRow';
import { IGridGuideServiceRepository } from '../interfaces/IGridGuideServiceRepository';
import { Log } from '../logging/Log';
import { TGridGuideRow, TOneLineGuideRow } from '../types';
import { TServicesParams } from '../types/TServicesParams';
import { DateTime } from '../utils';
import { UniqueIdentifier } from '../utils/UniqueIdentifier';

export class GridGuideServiceRepositoryImplV2 implements IGridGuideServiceRepository {
    private readonly TAG = 'GridGuideServiceRepositoryImplV2';

    private readonly HALF_HOUR_MS = 30 * 60 * 1000;

    private readonly mParams: TServicesParams;

    public constructor(params: TServicesParams) {
        this.mParams = params;
    }

    private createConfiguration(commonHeaders: Record<string, string>): Configuration {
        const cfg: ConfigurationParameters = {
            basePath: this.mParams.basePath,
            apiKey: this.mParams.accessToken,
            baseOptions: {
                timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                headers: commonHeaders,
            },
        };
        return new Configuration(cfg);
    }

    private static buildCommonHeaders(area: string) {
        return new CommonHeaders(area).headers;
    }

    private assertHalfHourAligned(dt: DateTime, name: string) {
        if (dt.getTime() % this.HALF_HOUR_MS !== 0) {
            throw new Error(`${name} should be in nearest half hour`);
        }
    }

    public async fetchProgramMetadata(
        stationId: string[],
        startTime: DateTime,
        endTime: DateTime,
    ): Promise<TGridGuideRow[]> {
        try {
            if (!Array.isArray(stationId) || stationId.length === 0) {
                throw new Error('stationId must be a non-empty array');
            }

            this.assertHalfHourAligned(startTime, 'startTime');
            this.assertHalfHourAligned(endTime, 'endTime');

            const startTimeInSeconds = Math.floor(startTime.getTime() / 1000);
            const endTimeInSeconds = Math.floor(endTime.getTime() / 1000);

            const commonHeaders = GridGuideServiceRepositoryImplV2.buildCommonHeaders(
                APPLICATION_FEATURE_AREA.GUIDE,
            );

            const requestParameters: GuideRowsGetV2Request = {
                applicationName: DeviceModel.getTiVoSettings().getAppName(),
                bodyId: this.mParams.bodyId,
                originRequestId: UniqueIdentifier.generateOriginRequestId(),
                msoPartnerId: DeviceModel.getTiVoSettings().getMsoPartnerId(),
                stationId: Array.from(new Set(stationId)).sort((a, b) => a.localeCompare(b)),
                windowStartTime: startTimeInSeconds,
                windowEndTime: endTimeInSeconds,
                applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
                applicationVersion: commonHeaders.ApplicationVersion,
                deviceType: commonHeaders.DeviceType,
                productName: commonHeaders.ProductName,
                xAmznRequestId: UniqueIdentifier.generateXAmznRequestId(),
                whiny: this.mParams.whiny || 'false',
            };

            if (!requestParameters.bodyId) {
                throw new Error('bodyId is required to fetch program metadata');
            }

            if (!requestParameters.msoPartnerId) {
                throw new Error('msoPartnerId is required to fetch program metadata');
            }

            const cfg = this.createConfiguration(commonHeaders);
            const gridGuideApi = new GuideApi(cfg);
            const gridGuideResponse = await gridGuideApi.guideRowsGetV2(requestParameters);

            const gridGuideResponseMap = new Map<string, TGridGuideRow>();
            gridGuideResponse.data.forEach((guideRow) => {
                gridGuideResponseMap.set(
                    guideRow.stationId,
                    new GridGuideRow(guideRow, this.mParams.imageHostBaseUrl),
                );
            });

            // return rows in requested UI order (preserve original stationId order)
            const gridGuideUiResponse: Array<TGridGuideRow> = [];
            stationId.forEach((id) => {
                const gridGuideRow = gridGuideResponseMap.get(id);
                if (gridGuideRow) gridGuideUiResponse.push(gridGuideRow);
            });

            return gridGuideUiResponse;
        } catch (error: any) {
            Log.e(this.TAG, 'fetchProgramMetadata()', error?.message ?? String(error), {
                stationId,
                windowStartTime: startTime,
                windowEndTime: endTime,
            });
            throw error;
        }
    }

    public async fetchOneLineGuideRowMetadata(
        stationId: string,
        anchorTime: DateTime,
        count: number,
        offset: number,
        pastMode: boolean,
        networkCallId?: number,
    ): Promise<TOneLineGuideRow> {
        try {
            this.assertHalfHourAligned(anchorTime, 'Anchor time');

            if (count < 0) {
                throw new Error('Count should be positive');
            }

            if (offset < 0) {
                throw new Error('Offset should be positive');
            }

            const anchorTimeInSeconds = Math.floor(anchorTime.getTime() / 1000);
            const commonHeaders = GridGuideServiceRepositoryImplV2.buildCommonHeaders(
                APPLICATION_FEATURE_AREA.PLAYER,
            );

            const requestParameters: OneLineGuideCellsGetV2Request = {
                applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
                applicationName: DeviceModel.getTiVoSettings().getAppName(),
                applicationVersion: commonHeaders.ApplicationVersion,
                bodyId: this.mParams.bodyId,
                deviceType: commonHeaders.DeviceType,
                originRequestId: UniqueIdentifier.generateOriginRequestId(),
                productName: commonHeaders.ProductName,
                msoPartnerId: DeviceModel.getTiVoSettings().getMsoPartnerId(),
                stationId,
                anchorTime: anchorTimeInSeconds,
                count,
                offset,
                pastMode,
                whiny: this.mParams.whiny || 'false',
                xAmznRequestId: UniqueIdentifier.generateXAmznRequestId(),
            };

            if (!requestParameters.bodyId) {
                throw new Error('bodyId is required to fetch one-line guide metadata');
            }

            if (!requestParameters.msoPartnerId) {
                throw new Error('msoPartnerId is required to fetch one-line guide metadata');
            }

            const cfg = this.createConfiguration(commonHeaders);
            const oneLineGuideApi = new OneLineGuideApi(cfg);
            const oneLineGuideResponse =
                await oneLineGuideApi.oneLineGuideCellsGetV2(requestParameters);

            return new OneLineGuideRow(
                oneLineGuideResponse?.data,
                this.mParams.imageHostBaseUrl,
                networkCallId,
            );
        } catch (error: any) {
            error.networkCallId = networkCallId;
            Log.e(this.TAG, 'fetchOneLineGuideRowMetadata()', error?.message ?? String(error), {
                stationId,
                count,
                offset,
                pastMode,
            });

            throw error;
        }
    }
}
